import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { getMyDevices } from "../../services/deviceService";
import "../teacher/TeacherLayer.css";

const MyDevicesLayer = () => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [summaryData, setSummaryData] = useState({
    school_name: "",
    school_id: null,
    total_devices: 0,
    active_devices: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const res = await getMyDevices();

      if (res && res.status) {
        setSummaryData({
          school_name: res.school_name || "",
          school_id: res.school_id || null,
          total_devices: res.total_devices || 0,
          active_devices: res.active_devices || 0,
        });

        const deviceList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.devices)
          ? res.devices
          : [];
        setDevices(deviceList);
      } else {
        const deviceList = res?.devices || res?.data || (Array.isArray(res) ? res : []);
        if (Array.isArray(deviceList)) {
          setDevices(deviceList);
        } else {
          setDevices([]);
        }
      }
    } catch (error) {
      toast.error("Failed to load school devices");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDevices = devices.filter((dev) => {
    const term = searchTerm.toLowerCase();
    const macId = (dev.mac_id || dev.mac_address || "").toLowerCase();
    const label = (dev.label || dev.device_name || dev.name || "").toLowerCase();
    const deviceId = String(dev.id || dev.device_id || "").toLowerCase();
    const status = (dev.status || "").toLowerCase();
    return (
      macId.includes(term) ||
      label.includes(term) ||
      deviceId.includes(term) ||
      status.includes(term)
    );
  });

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          <div className="admin-page-header list-header d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3">
            <div>
              <h5 className="admin-page-title d-flex align-items-center gap-2">
                <Icon icon="solar:devices-bold-duotone" className="text-primary fs-3" /> My Devices
              </h5>
              <p className="text-secondary-light m-0 mt-1" style={{ fontSize: "14px" }}>
                Connected biometric attendance devices and RFID hardware for {summaryData.school_name || "your school"}.
              </p>
            </div>
            <div>
              <button
                className="btn btn-outline-primary d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm"
                onClick={fetchDevices}
                disabled={loading}
              >
                <Icon icon="mdi:refresh" className={loading ? "spin" : ""} />
                {loading ? "Refreshing..." : "Refresh Devices"}
              </button>
            </div>
          </div>

          {/* Device Summary Metrics Cards */}
          <div className="row g-3 my-3">
            <div className="col-12 col-sm-6 col-md-4">
              <div className="p-3 border rounded-3 bg-light shadow-sm d-flex align-items-center gap-3">
                <div className="p-3 rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                  <Icon icon="solar:devices-bold" fontSize="24px" />
                </div>
                <div>
                  <div className="text-muted small fw-semibold">TOTAL DEVICES</div>
                  <div className="fs-4 fw-bold text-dark">{summaryData.total_devices || devices.length || 0}</div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4">
              <div className="p-3 border rounded-3 bg-light shadow-sm d-flex align-items-center gap-3">
                <div className="p-3 rounded-circle bg-success bg-opacity-10 text-success d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                  <Icon icon="solar:check-circle-bold" fontSize="24px" />
                </div>
                <div>
                  <div className="text-muted small fw-semibold">ACTIVE / ASSIGNED</div>
                  <div className="fs-4 fw-bold text-success">{summaryData.active_devices || devices.filter(d => d.status === 'assigned' || d.status === 'active' || d.status === 'online').length || 0}</div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4">
              <div className="p-3 border rounded-3 bg-light shadow-sm d-flex align-items-center gap-3">
                <div className="p-3 rounded-circle bg-info bg-opacity-10 text-info d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                  <Icon icon="solar:buildings-2-bold" fontSize="24px" />
                </div>
                <div>
                  <div className="text-muted small fw-semibold">SCHOOL NAME</div>
                  <div className="fs-5 fw-bold text-dark text-truncate" style={{ maxWidth: "180px" }}>{summaryData.school_name || `School #${summaryData.school_id || 54}`}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="row my-4 align-items-center">
            <div className="col-md-6 col-lg-5">
              <div className="input-group shadow-sm rounded-pill overflow-hidden border">
                <span className="input-group-text bg-white border-0 ps-3">
                  <Icon icon="mdi:magnify" className="text-muted" fontSize="20px" />
                </span>
                <input
                  type="text"
                  className="form-control border-0 ps-0 shadow-none"
                  placeholder="Search by MAC ID, Label, Status, or Device ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table border rounded-3 shadow-sm">
            <table className="user-table align-middle">
              <thead>
                <tr className="text-center align-middle">
                  <th style={{ width: "60px" }}>#</th>
                  <th style={{ width: "80px" }}>Device ID</th>
                  <th>MAC Address</th>
                  <th>Label / Name</th>
                  <th>Status</th>
                  <th>Last Seen At</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody className="text-center align-middle">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="py-4 text-muted">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                      Fetching device details from server...
                    </td>
                  </tr>
                ) : filteredDevices.length > 0 ? (
                  filteredDevices.map((item, index) => {
                    const statusVal = String(item.status || "unassigned").toLowerCase();
                    const isAssigned = statusVal === "assigned" || statusVal === "active" || statusVal === "online" || statusVal === "1";

                    return (
                      <tr key={item.id || index} className="smooth-table-row">
                        <td className="fw-semibold text-muted">{index + 1}</td>
                        <td className="fw-bold text-primary">#{item.id || index + 1}</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded text-primary fw-bold" style={{ letterSpacing: "1px" }}>
                            {item.mac_id || item.mac_address || "N/A"}
                          </code>
                        </td>
                        <td className="fw-bold text-dark">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Icon icon="mdi:laptop-chromebook" className="text-secondary" fontSize="18px" />
                            <span>{item.label || item.device_name || item.name || "Attendance Terminal"}</span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge rounded-pill px-3 py-1.5 ${
                              isAssigned
                                ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25"
                                : "bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25"
                            }`}
                          >
                            <Icon
                              icon={isAssigned ? "mdi:check-circle-outline" : "mdi:clock-outline"}
                              className="me-1"
                            />
                            {isAssigned ? (item.status ? item.status.toUpperCase() : "ASSIGNED") : "UNASSIGNED"}
                          </span>
                        </td>
                        <td className="text-secondary fw-medium">
                          {item.last_seen_at || item.last_ping || "N/A"}
                        </td>
                        <td className="text-muted small">
                          {item.created_at || "N/A"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="py-5 text-muted">
                      <Icon icon="solar:devices-outline" className="fs-1 text-secondary mb-2 d-block mx-auto" />
                      No devices found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyDevicesLayer;
